import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { makeStyles } from "@material-ui/core/styles";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React examples
import Sidenav from "features/Sidenav";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import Drawer from "@material-ui/core/Drawer";

// Material Dashboard 2 PRO React routes
import routes from "routes";

import menus from "menus";

// Material Dashboard 2 PRO React contexts
import {
  useMaterialUIController,
  setOpenPhone,
  setActivePage,
  setAuthentication,
} from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import ManualDailingBox from "application/components/CTLAgentDashboard/manualDailingBox";
import SIPPhone from "application/components/SIPPhone";
import { QueryClient, QueryClientProvider } from "react-query";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import CTLSideBar from "application/components/CTLSideBar";
import Illustration from "application/modules/authentication/sign-in";
import EmbeddedLogin from "application/modules/authentication/EmbeddedLogin";
import Draggable from "react-draggable";
import { setIsPhoneShowAlways } from "context";
import EAMSLogin from "application/modules/authentication/EAMSLogin";
import LoginWithKey from "application/modules/authentication/LoginWithKey";
import ActiveConferenceParticipants from "application/modules/ActiveConferenceParticipants";
import Dispatcher from "application/modules/Dispatcher";

const useStyles = makeStyles({
  drawer: {
    width: 480,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  drawerPaper: {
    width: 480,
    left: "calc(50% - 240px)",
    background: "transparent",
  },
  minimizedPhone: {
    position: "fixed",
    top: "110px",
    width: 350,
    left: "calc(50% - 200px)",
    transform: "translateX(-50%)",
    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    borderRadius: "10px",
    cursor: "move",
    zIndex: 1000,
  },
});

export default function App() {
  const queryClient = new QueryClient();
  const [controller, dispatch] = useMaterialUIController();
  const {
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    openPhone,
    activePage,
    authentication,
    isActiveCall,
    isPhoneShowAlways,
  } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const classes = useStyles();
  const [displaySideNav, setDisplaySideNav] = useState(null);
  const Authenticated = AuthenticationService.isAuthenticated();
  const ChangePasswordRequired =
    AuthenticationService.getChangePasswordRequired();
  const enableCRM = AuthenticationService.allowExternalCRM();
  const enableEAMS = AuthenticationService.allowEamsMenu();
  const isDispatcherRole =
    AuthenticationService.allowRailwayDispatcher() ||
    AuthenticationService.allowMCXDispatcher();

  const disableSidebar = AuthenticationService.getKeyTypePage();
  const MemoizedSIPPhone = SIPPhone; // Disable memoization

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    setDisplaySideNav(authentication);
    if (!authentication && Authenticated) {
      setAuthentication(dispatch, Authenticated);
    }
  }, [authentication, layout]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const handlePhone = useCallback(() => {
    if (isActiveCall) {
      setIsPhoneShowAlways(dispatch, true);
    } else {
      setOpenPhone(dispatch, !openPhone);
      setIsPhoneShowAlways(dispatch, false);
    }
  }, [isActiveCall, openPhone, dispatch]);

  useEffect(() => {
    if (!isActiveCall) {
      setIsPhoneShowAlways(dispatch, false);
    }
  }, [isActiveCall]);

  const navigateToHomePage = () => {
    activePage.key = "home.dashboard";
    activePage.name = "Dashboard";
    activePage.url = "home/app";
    activePage.description = "User Dashboard";
    setActivePage(dispatch, activePage);
    return true;
  };

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {authentication && layout === "dashboard" && (
          <>
            <QueryClientProvider client={queryClient}>
              {!disableSidebar &&
                !ChangePasswordRequired &&
                !enableCRM &&
                !enableEAMS &&
                !isDispatcherRole && (
                  <CTLSideBar
                    color={sidenavColor}
                    brand={darkMode ? brandWhite : brandDark}
                    brandName="Coral Telecom Ltd"
                    routes={menus}
                  ></CTLSideBar>
                )}
            </QueryClientProvider>
          </>
        )}
        <Routes>
          {getRoutes(routes)}
          <Route path="/ActiveConference" element={<ActiveConferenceParticipants />} />
          <Route path="/embedded" element={<EmbeddedLogin />} />
          <Route path="/dispatcher" element={<Dispatcher />} />
          <Route path="/loginWithKey/:key" element={<LoginWithKey />} />
          <Route path="/eams" element={<EAMSLogin />} />
          <Route path="*" element={<Navigate to="/app/home" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {displaySideNav && layout === "dashboard" && (
        <>
          <QueryClientProvider client={queryClient}>
            {!disableSidebar &&
              !ChangePasswordRequired &&
              !enableCRM &&
              !enableEAMS &&
              !isDispatcherRole && (
                <CTLSideBar
                  color={sidenavColor}
                  brand={darkMode ? brandWhite : brandDark}
                  brandName="Coral Telecom Ltd"
                  routes={menus}
                />
              )}
          </QueryClientProvider>
        </>
      )}
      {/* {isPhoneShowAlways ? (
        <Draggable>
          <div className={classes.minimizedPhone}>
            <MemoizedSIPPhone />
          </div>
        </Draggable>
      ) : ( */}
      <Drawer
        variant="temporary"
        anchor="top"
        open={openPhone}
        onClose={handlePhone}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawer}>
          <MemoizedSIPPhone />
        </div>
      </Drawer>
      {/* )} */}

      {enableCRM ? (
        <ManualDailingBox activePage={activePage} />
      ) : (
        <Routes>
          {getRoutes(routes)}
            <Route path="/ActiveConference" element={<ActiveConferenceParticipants />} />
          <Route path="/embedded" element={<EmbeddedLogin />} />
          <Route path="/dispatcher" element={<Dispatcher />} />
          <Route path="/loginWithKey/:key" element={<LoginWithKey />} />
          <Route path="/eams" element={<EAMSLogin />} />
          <Route path="*" element={<Illustration />} />
        </Routes>
      )}
    </ThemeProvider>
  );
}
